import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Building2, Home, Sprout } from 'lucide-react';
import { services } from '../mockData';

const ServicesPage = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('All');

  // Logic to handle the "Header Dropdown" clicks automatically
  useEffect(() => {
    const categoryParam = searchParams.get('cat');
    if (categoryParam === 'mining' || categoryParam === 'corporate') {
      setActiveTab('Corporate');
    } else if (categoryParam === 'residential' || categoryParam === 'wholesale') {
      setActiveTab('Residential');
    }
  }, [searchParams]);

  // Helper to categorize services (In case mockData isn't fully updated yet)
  // We manually assign IDs to categories here to ensure the filter works immediately
  const getCategoryForService = (id) => {
    const corporateIds = [1, 2, 4]; // Miyawaki, Industrial, Maintenance
    if (corporateIds.includes(id)) return 'Corporate';
    return 'Residential';
  };

  const filteredServices = activeTab === 'All' 
    ? services 
    : services.filter(service => {
        // Check if the service has a category property, otherwise use helper
        const category = service.category || getCategoryForService(service.id);
        return category === activeTab;
      });

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E5631] to-[#2d7a47] py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1558904541-efa843a96f01"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl text-white">
            <span className="inline-block bg-[#4CAF50] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Our Expertise
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
              Ecological & Aesthetic Solutions
            </h1>
            <p className="text-xl text-green-100 leading-relaxed">
              From large-scale mine reclamation to luxury private gardens.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="sticky top-20 z-40 bg-white border-b border-gray-100 shadow-sm py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap justify-center gap-4">
          <button
            onClick={() => setActiveTab('All')}
            className={`flex items-center px-6 py-3 rounded-full font-bold transition-all duration-300 ${
              activeTab === 'All' 
                ? 'bg-[#1E5631] text-white shadow-lg' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Sprout className="w-4 h-4 mr-2" />
            All Services
          </button>
          <button
            onClick={() => setActiveTab('Corporate')}
            className={`flex items-center px-6 py-3 rounded-full font-bold transition-all duration-300 ${
              activeTab === 'Corporate' 
                ? 'bg-[#1E5631] text-white shadow-lg' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Building2 className="w-4 h-4 mr-2" />
            Corporate & Mining
          </button>
          <button
            onClick={() => setActiveTab('Residential')}
            className={`flex items-center px-6 py-3 rounded-full font-bold transition-all duration-300 ${
              activeTab === 'Residential' 
                ? 'bg-[#1E5631] text-white shadow-lg' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Home className="w-4 h-4 mr-2" />
            Residential & Retail
          </button>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {filteredServices.length > 0 ? (
              filteredServices.map((service, index) => (
                <div
                  key={service.id}
                  className={`bg-white rounded-3xl overflow-hidden shadow-xl grid grid-cols-1 lg:grid-cols-2 gap-0 items-center transform transition-all duration-500 hover:-translate-y-2 ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Image Side */}
                  <div className={`h-full min-h-[400px] relative ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <img
                      src={`${service.image}?auto=format&fit=crop&w=800&q=80`}
                      alt={service.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/10"></div>
                  </div>

                  {/* Content Side */}
                  <div className={`p-8 lg:p-12 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <span className={`inline-block px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-6 ${
                      (service.category || getCategoryForService(service.id)) === 'Corporate' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {service.category || getCategoryForService(service.id)}
                    </span>
                    
                    <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                      {service.title}
                    </h2>
                    <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                      {service.description}
                    </p>
                    
                    <div className="space-y-4 mb-8">
                      <h3 className="text-lg font-semibold text-[#1E5631]">Key Features:</h3>
                      {service.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-[#4CAF50] mr-3 flex-shrink-0 mt-1" />
                          <span className="text-gray-700">{benefit}</span>
                        </div>
                      ))}
                    </div>

                    <Link 
                      to="/contact"
                      className="inline-flex items-center text-[#1E5631] font-bold hover:text-[#4CAF50] transition-colors"
                    >
                      Request this Service <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-20">
                <p className="text-xl text-gray-500">No services found for this category.</p>
                <button onClick={() => setActiveTab('All')} className="mt-4 text-[#1E5631] font-bold">View All Services</button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#4CAF50] font-semibold text-sm uppercase tracking-wide mb-4">
              Our Process
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How We Execute
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Scientific planning meets artistic execution
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { num: '1', title: 'Site Analysis', desc: 'Soil testing, topography check, and requirement gathering.' },
              { num: '2', title: 'Design & Plan', desc: '3D rendering and selection of native/adaptive plant species.' },
              { num: '3', title: 'Execution', desc: 'Soil preparation, plantation, and hardscaping installation.' },
              { num: '4', title: 'Maintenance', desc: 'Regular monitoring to ensure high survival rates and growth.' }
            ].map((step, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100">
                <div className="bg-gradient-to-br from-[#1E5631] to-[#2d7a47] text-white w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold mb-6">
                  {step.num}
                </div>
                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#1E5631] to-[#2d7a47] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1466692476868-aef1dfb1e735"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Whether it's a 100-acre afforestation project or a private farmhouse.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-[#4CAF50] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#45a049] transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
          >
            Get Free Quote
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;